/**
 * K/R/V-Verknüpfungszeile (Detail-Remake) — Typen + REINE Kaskadenlogik,
 * bewusst ohne React/Fetch (vitest-testbar, s. linkSlots.test.ts).
 *
 * Regeln (User-Vorgabe):
 *  - Kontakt gewählt → Risiko-/Vertragsauswahl beschränkt sich auf den Kontakt;
 *    beim KontaktWECHSEL werden Risiko + Vertrag geleert (Zugehörigkeit zum
 *    neuen Kontakt ist nicht garantiert).
 *  - Risiko gewählt → Kontakt wird (falls leer) automatisch gefüllt; ein
 *    gesetzter Vertrag wird geleert, wenn das Risiko wechselt.
 *  - Vertrag gewählt → Kontakt + Risiko werden (falls leer) automatisch gefüllt
 *    (Risiko nur bei eindeutiger Zuordnung — liefert der resolve-link-Endpoint).
 *  - Kontakt geleert → alles leeren (Risiko/Vertrag implizieren den Kontakt).
 *  - Risiko geleert → Vertrag bleibt (Vertrag hängt am Kontakt, nicht zwingend
 *    am Risiko); Kontakt bleibt.
 *
 * Mehrfach-Zuordnungen (SP3, B7/B16): `risiken`/`vertraege` tragen ALLE
 * Zuordnungen; `risiko`/`vertrag` sind der Anzeige-Slot für Position 0
 * (Invariante: risiko === risiken[0] ?? null). Die Picker-Auswahl ersetzt
 * Position 0 und behält weitere Zuordnungen. Patches senden Singular + Array;
 * das Backend (reconcileMultiLinks) hält beide konsistent.
 *
 * Verwaltungs-Popover (SP5): die add-/remove-/replace-Funktionen verwalten
 * die Listen EXPLIZIT — ohne Kaskaden-Leerung der jeweils anderen Sorte (der
 * User sieht und bearbeitet alle Einträge selbst). Einzige Ausnahme: ein
 * leerer Kontakt wird beim Hinzufügen weiterhin aus der resolve-link-Antwort
 * gefüllt (ein Vorgang braucht immer einen Kontakt).
 */

export interface KontaktRef {
    id: number;
    lastname?: string | null;
    firstName?: string | null;
    /** ContactType.type — 'Firma' | 'Person' (Badge im Picker). */
    typ?: string | null;
    /** ContactStatus.status — 'Kunde' | 'Interessent' | … (Badge im Picker). */
    kundeStatus?: string | null;
    /** Standard-Adresse (PO 10.08.2026) — klein neben dem Namen im Picker,
     *  damit gleichnamige Kontakte auseinanderzuhalten sind. */
    adresse?: { street?: string | null; postalCode?: string | null; city?: string | null } | null;
    boType?: "NONE" | "USER" | "TEAM" | null;
    boTeam?: { id: string; name?: string | null } | null;
    boUser?: { userId: string; kuerzel?: string | null; displayName: string } | null;
    foUser?: { userId: string; kuerzel?: string | null; displayName: string } | null;
}

export interface RisikoRef {
    id: number;
    name?: string | null;
    riskType?: string | null;
    riskObjectIdentifier?: string | null;
    contractNbr?: string | null;
    contactName?: string | null;
}

export interface VertragRef {
    id: number;
    contractNbr?: string | null;
    branch?: string | null;
    premiumAnnualGross?: number | null;
    riskObjects?: Array<{ id?: number; name?: string | null; riskType?: string | null; riskObjectIdentifier?: string | null }>;
}

export interface LinkSlots {
    kontakt: KontaktRef | null;
    /** Alle Kontakte; `kontakt` steht an Position 0 (technisch der `kunde`). */
    kontakte?: KontaktRef[];
    /** Anzeige-Slot = Position 0 von `risiken`. */
    risiko: RisikoRef | null;
    /** Anzeige-Slot = Position 0 von `vertraege`. */
    vertrag: VertragRef | null;
    /** ALLE Risiko-Zuordnungen (SP3). */
    risiken: RisikoRef[];
    /** ALLE Vertrag-Zuordnungen (SP3). */
    vertraege: VertragRef[];
}

/** Persistenz-Patch für PUT /vorgang/:id bzw. PUT /todo/:id. */
export interface LinkPatch {
    contactId?: number | null;
    contactIds?: number[];
    riskObjectId?: number | null;
    contractId?: number | null;
    /** Mehrfach-Zuordnungen (SP3) — Quelle der Wahrheit im Backend. */
    riskObjectIds?: number[];
    contractIds?: number[];
}

/**
 * Derselbe Patch als Body für PUT /vorgang/:id.
 *
 * Der Vorgang trägt seinen Kontakt über die `kunde`-Relation (Spalte
 * `kundeId`); ein Feld `contactId` hat er NICHT — am To-do heißt dasselbe
 * Feld wirklich so, und dorthin geht der Patch deshalb unverändert. Wer den
 * Vorgangs-Patch roh sendet, bekommt keinen 400, sondern einen 500: TypeORM
 * wirft in `repo.update()` auf der unbekannten Property, bevor irgendeine
 * Validierung greift. Genau daran scheiterte der Kontaktwechsel in der neuen
 * Ansicht (PO 07.09.2026: „internal server error, wenn wir den Kontakt ändern
 * wollen bei einem Vorgang").
 *
 * `undefined` heißt „nicht angefasst", `null` heißt „entfernen" — deshalb
 * wandert jedes Feld nur bei `!== undefined` in den Body.
 */
export function vorgangBodyAusPatch(patch: LinkPatch): Record<string, unknown> {
    const body: Record<string, unknown> = {};
    if (patch.contactId !== undefined) {
        body.kunde = patch.contactId != null ? { id: patch.contactId } : null;
    }
    if (patch.contactIds !== undefined) body.contactIds = patch.contactIds;
    if (patch.riskObjectId !== undefined) body.riskObjectId = patch.riskObjectId;
    if (patch.contractId !== undefined) body.contractId = patch.contractId;
    // Mehrfach-Zuordnungen (SP3) — Arrays sind die Quelle der Wahrheit, das
    // Backend spiegelt Position 0 in die Singular-Spalten.
    if (patch.riskObjectIds !== undefined) body.riskObjectIds = patch.riskObjectIds;
    if (patch.contractIds !== undefined) body.contractIds = patch.contractIds;
    return body;
}

export const EMPTY_SLOTS: LinkSlots = {
    kontakt: null,
    risiko: null,
    vertrag: null,
    risiken: [],
    vertraege: [],
};

export function kontaktLabel(k?: KontaktRef | null): string {
    if (!k) return "—";
    const name = [k.lastname, k.firstName].filter(Boolean).join(", ");
    return name || `Kontakt ${k.id}`;
}

/** Einzeilige Standard-Adresse für den Picker („Musterstr. 1, 50667 Köln"),
 *  ohne leere Bestandteile; ganz ohne Adressdaten → null (dann wird nichts
 *  angezeigt). */
export function kontaktAdresse(k?: KontaktRef | null): string | null {
    const a = k?.adresse;
    if (!a) return null;
    const street = (a.street ?? "").trim();
    const ort = [(a.postalCode ?? "").trim(), (a.city ?? "").trim()].filter(Boolean).join(" ");
    const text = [street, ort].filter(Boolean).join(", ");
    return text || null;
}

export function risikoLabel(r?: RisikoRef | null): string {
    if (!r) return "—";
    return r.name || `Risiko ${r.id}`;
}

export function vertragLabel(v?: VertragRef | null): string {
    if (!v) return "—";
    return v.contractNbr || `Vertrag ${v.id}`;
}

/** Ergebnis eines Kaskaden-Schritts: neuer Slot-Zustand + minimaler Persistenz-Patch. */
export interface CascadeResult {
    slots: LinkSlots;
    patch: LinkPatch;
}

/** Kontakt gewählt/gewechselt: Risiken + Verträge leeren, wenn der Kontakt wechselt. */
export function applyKontaktSelection(prev: LinkSlots, kontakt: KontaktRef): CascadeResult {
    const contactChanged = prev.kontakt?.id !== kontakt.id;
    const slots: LinkSlots = {
        kontakt,
        risiko: contactChanged ? null : prev.risiko,
        vertrag: contactChanged ? null : prev.vertrag,
        risiken: contactChanged ? [] : prev.risiken,
        vertraege: contactChanged ? [] : prev.vertraege,
    };
    const patch: LinkPatch = { contactId: kontakt.id };
    if (contactChanged && (prev.risiko || prev.risiken.length)) {
        patch.riskObjectId = null;
        patch.riskObjectIds = [];
    }
    if (contactChanged && (prev.vertrag || prev.vertraege.length)) {
        patch.contractId = null;
        patch.contractIds = [];
    }
    return { slots, patch };
}

/**
 * Ersetzt die Kontaktliste — JEDER Kontakt ist abwählbar (PO 28.09.2026: „das
 * Konzept führend ist nicht wichtig"). Bis dahin war der erste Kontakt
 * („führend") im Dialog gesperrt und ließ sich weder abwählen noch ersetzen.
 *
 * Ein Kontakt steht trotzdem vorn: der Vorgang trägt seinen Kunden technisch
 * über `kunde` (= Position 0). Bleibt der bisherige in der Auswahl, behält er
 * den Platz — sonst rückt der erste der neuen Auswahl nach. Eine leere Auswahl
 * entfernt den Kunden ganz. Risiken und Verträge bleiben dabei stehen: die
 * pflegt der Nutzer im selben Dialog selbst, eine stille Kaskade würde dort
 * Zuordnungen verwerfen, die er gar nicht angefasst hat.
 */
export function applyKontaktMultiSelection(
    prev: LinkSlots,
    nextKontakte: KontaktRef[],
): CascadeResult {
    const unique = nextKontakte.filter((k, i, all) => all.findIndex((x) => x.id === k.id) === i);
    const bisher = prev.kontakt ? unique.find((k) => k.id === prev.kontakt!.id) ?? null : null;
    const vorn = bisher ?? unique[0] ?? null;
    const kontakte = vorn ? [vorn, ...unique.filter((k) => k.id !== vorn.id)] : [];
    return {
        slots: { ...prev, kontakt: vorn, kontakte },
        patch: {
            contactId: vorn?.id ?? null,
            contactIds: kontakte.map((k) => k.id),
        },
    };
}

/**
 * Risiko gewählt: Kontakt (falls leer) aus `resolvedKontakt` übernehmen; ein
 * gesetzter Vertrag wird beim RisikoWECHSEL geleert. Die Auswahl ersetzt
 * Position 0 der Risiken; weitere Zuordnungen bleiben (dedupliziert) erhalten.
 */
export function applyRisikoSelection(
    prev: LinkSlots,
    risiko: RisikoRef,
    resolvedKontakt?: KontaktRef | null,
): CascadeResult {
    const riskChanged = prev.risiko?.id !== risiko.id;
    const kontakt = prev.kontakt ?? resolvedKontakt ?? null;
    const risiken = [risiko, ...prev.risiken.slice(1).filter((r) => r.id !== risiko.id)];
    const slots: LinkSlots = {
        kontakt,
        risiko,
        vertrag: riskChanged ? null : prev.vertrag,
        risiken,
        vertraege: riskChanged ? [] : prev.vertraege,
    };
    const patch: LinkPatch = { riskObjectId: risiko.id, riskObjectIds: risiken.map((r) => r.id) };
    if (!prev.kontakt && kontakt) patch.contactId = kontakt.id;
    if (riskChanged && (prev.vertrag || prev.vertraege.length)) {
        patch.contractId = null;
        patch.contractIds = [];
    }
    return { slots, patch };
}

/**
 * Vertrag gewählt: Kontakt + Risiko (falls leer) aus der resolve-link-Antwort
 * übernehmen (Risiko nur bei eindeutiger Zuordnung). Die Auswahl ersetzt
 * Position 0 der Verträge; weitere Zuordnungen bleiben (dedupliziert) erhalten.
 */
export function applyVertragSelection(
    prev: LinkSlots,
    vertrag: VertragRef,
    resolved?: { kontakt?: KontaktRef | null; risiko?: RisikoRef | null },
): CascadeResult {
    const kontakt = prev.kontakt ?? resolved?.kontakt ?? null;
    const risiko = prev.risiko ?? resolved?.risiko ?? null;
    const vertraege = [vertrag, ...prev.vertraege.slice(1).filter((v) => v.id !== vertrag.id)];
    const risiken = prev.risiko ? prev.risiken : risiko ? [risiko] : [];
    const slots: LinkSlots = { kontakt, risiko, vertrag, risiken, vertraege };
    const patch: LinkPatch = { contractId: vertrag.id, contractIds: vertraege.map((v) => v.id) };
    if (!prev.kontakt && kontakt) patch.contactId = kontakt.id;
    if (!prev.risiko && risiko) {
        patch.riskObjectId = risiko.id;
        patch.riskObjectIds = [risiko.id];
    }
    return { slots, patch };
}

/**
 * Weitere Risiko-Zuordnung anhängen (Verwaltungs-Popover, B7/B16): Position 0
 * bleibt unverändert, Duplikate werden ignoriert (leerer Patch = kein PUT).
 * Leerer Bestand delegiert an applyRisikoSelection (inkl. deren Kaskade).
 */
export function addRisiko(
    prev: LinkSlots,
    risiko: RisikoRef,
    resolvedKontakt?: KontaktRef | null,
): CascadeResult {
    if (!prev.risiken.length) return applyRisikoSelection(prev, risiko, resolvedKontakt);
    if (prev.risiken.some((r) => r.id === risiko.id)) return { slots: prev, patch: {} };
    const risiken = [...prev.risiken, risiko];
    const kontakt = prev.kontakt ?? resolvedKontakt ?? null;
    const slots: LinkSlots = { ...prev, kontakt, risiken };
    const patch: LinkPatch = { riskObjectIds: risiken.map((r) => r.id) };
    if (!prev.kontakt && kontakt) patch.contactId = kontakt.id;
    return { slots, patch };
}

/** Weitere Vertrag-Zuordnung anhängen — Gegenstück zu addRisiko. */
export function addVertrag(
    prev: LinkSlots,
    vertrag: VertragRef,
    resolvedKontakt?: KontaktRef | null,
): CascadeResult {
    if (!prev.vertraege.length) return applyVertragSelection(prev, vertrag, { kontakt: resolvedKontakt });
    if (prev.vertraege.some((v) => v.id === vertrag.id)) return { slots: prev, patch: {} };
    const vertraege = [...prev.vertraege, vertrag];
    const kontakt = prev.kontakt ?? resolvedKontakt ?? null;
    const slots: LinkSlots = { ...prev, kontakt, vertraege };
    const patch: LinkPatch = { contractIds: vertraege.map((v) => v.id) };
    if (!prev.kontakt && kontakt) patch.contactId = kontakt.id;
    return { slots, patch };
}

/** Ersetzt die komplette Risiko-Liste aus einem Mehrfach-Dialog. */
export function applyRisikoMultiSelection(
    prev: LinkSlots,
    nextRisiken: RisikoRef[],
    resolvedKontakt?: KontaktRef | null,
): CascadeResult {
    const risiken = nextRisiken.filter((r, i, arr) => arr.findIndex((x) => x.id === r.id) === i);
    const risiko = risiken[0] ?? null;
    const kontakt = prev.kontakt ?? resolvedKontakt ?? null;
    const slots: LinkSlots = { ...prev, kontakt, risiko, risiken };
    const patch: LinkPatch = {
        riskObjectId: risiko?.id ?? null,
        riskObjectIds: risiken.map((r) => r.id),
    };
    if (!prev.kontakt && kontakt) patch.contactId = kontakt.id;
    return { slots, patch };
}

/** Ersetzt die komplette Vertrags-Liste aus einem Mehrfach-Dialog. */
export function applyVertragMultiSelection(
    prev: LinkSlots,
    nextVertraege: VertragRef[],
    resolved?: { kontakt?: KontaktRef | null; risiko?: RisikoRef | null },
): CascadeResult {
    const vertraege = nextVertraege.filter((v, i, arr) => arr.findIndex((x) => x.id === v.id) === i);
    const vertrag = vertraege[0] ?? null;
    const kontakt = prev.kontakt ?? resolved?.kontakt ?? null;
    const risiko = prev.risiko ?? resolved?.risiko ?? null;
    const risiken = prev.risiko ? prev.risiken : risiko ? [risiko] : prev.risiken;
    const slots: LinkSlots = { ...prev, kontakt, risiko, risiken, vertrag, vertraege };
    const patch: LinkPatch = {
        contractId: vertrag?.id ?? null,
        contractIds: vertraege.map((v) => v.id),
    };
    if (!prev.kontakt && kontakt) patch.contactId = kontakt.id;
    if (!prev.risiko && risiko) {
        patch.riskObjectId = risiko.id;
        patch.riskObjectIds = [risiko.id];
    }
    return { slots, patch };
}

/**
 * Kombinierte Auswahl aus dem Tab-Popup (Risiko + Vertrag) in EINEM Schritt
 * (Zuweisung „Tief", T1): ersetzt die jeweils ÜBERGEBENE Liste (risiken/
 * vertraege) vollständig und liefert EINEN gemergten Patch → genau ein PUT.
 * Eine nicht übergebene Sorte (undefined) bleibt unangetastet. Ein leerer
 * Kontakt wird — wie bei den Einzel-Kaskaden — aus `resolved` übernommen
 * (ein Vorgang braucht immer einen Kontakt). Duplikate je Liste werden entfernt.
 */
export function applyKombiSelection(
    prev: LinkSlots,
    next: { risiken?: RisikoRef[]; vertraege?: VertragRef[] },
    resolved?: { kontakt?: KontaktRef | null },
): CascadeResult {
    const kontakt = prev.kontakt ?? resolved?.kontakt ?? null;
    const slots: LinkSlots = { ...prev, kontakt };
    const patch: LinkPatch = {};
    if (!prev.kontakt && kontakt) patch.contactId = kontakt.id;

    if (next.risiken) {
        const risiken = next.risiken.filter((r, i, arr) => arr.findIndex((x) => x.id === r.id) === i);
        slots.risiken = risiken;
        slots.risiko = risiken[0] ?? null;
        patch.riskObjectId = risiken[0]?.id ?? null;
        patch.riskObjectIds = risiken.map((r) => r.id);
    }
    if (next.vertraege) {
        const vertraege = next.vertraege.filter((v, i, arr) => arr.findIndex((x) => x.id === v.id) === i);
        slots.vertraege = vertraege;
        slots.vertrag = vertraege[0] ?? null;
        patch.contractId = vertraege[0]?.id ?? null;
        patch.contractIds = vertraege.map((v) => v.id);
    }
    return { slots, patch };
}

/** Einzelne Risiko-Zuordnung entfernen; die nächste rückt als Haupt nach. */
export function removeRisiko(prev: LinkSlots, risikoId: number): CascadeResult {
    const risiken = prev.risiken.filter((r) => r.id !== risikoId);
    const risiko = risiken[0] ?? null;
    const slots: LinkSlots = { ...prev, risiko, risiken };
    const patch: LinkPatch = { riskObjectIds: risiken.map((r) => r.id) };
    if ((risiko?.id ?? null) !== (prev.risiko?.id ?? null)) patch.riskObjectId = risiko?.id ?? null;
    return { slots, patch };
}

/** Einzelne Vertrag-Zuordnung entfernen; die nächste rückt als Haupt nach. */
export function removeVertrag(prev: LinkSlots, vertragId: number): CascadeResult {
    const vertraege = prev.vertraege.filter((v) => v.id !== vertragId);
    const vertrag = vertraege[0] ?? null;
    const slots: LinkSlots = { ...prev, vertrag, vertraege };
    const patch: LinkPatch = { contractIds: vertraege.map((v) => v.id) };
    if ((vertrag?.id ?? null) !== (prev.vertrag?.id ?? null)) patch.contractId = vertrag?.id ?? null;
    return { slots, patch };
}

/**
 * Zuordnung an ihrer Position ersetzen (Verwaltungs-Popover, je Zeile
 * „ändern"): taucht die neue Auswahl bereits an anderer Stelle auf, wird
 * der Duplikat-Eintrag entfernt (erste Position gewinnt).
 */
export function replaceRisiko(
    prev: LinkSlots,
    oldId: number,
    next: RisikoRef,
    resolvedKontakt?: KontaktRef | null,
): CascadeResult {
    const risiken = prev.risiken
        .map((r) => (r.id === oldId ? next : r))
        .filter((r, i, arr) => arr.findIndex((x) => x.id === r.id) === i);
    const risiko = risiken[0] ?? null;
    const kontakt = prev.kontakt ?? resolvedKontakt ?? null;
    const slots: LinkSlots = { ...prev, kontakt, risiko, risiken };
    const patch: LinkPatch = { riskObjectIds: risiken.map((r) => r.id) };
    if ((risiko?.id ?? null) !== (prev.risiko?.id ?? null)) patch.riskObjectId = risiko?.id ?? null;
    if (!prev.kontakt && kontakt) patch.contactId = kontakt.id;
    return { slots, patch };
}

/** Vertrag-Zuordnung an ihrer Position ersetzen — Gegenstück zu replaceRisiko. */
export function replaceVertrag(
    prev: LinkSlots,
    oldId: number,
    next: VertragRef,
    resolvedKontakt?: KontaktRef | null,
): CascadeResult {
    const vertraege = prev.vertraege
        .map((v) => (v.id === oldId ? next : v))
        .filter((v, i, arr) => arr.findIndex((x) => x.id === v.id) === i);
    const vertrag = vertraege[0] ?? null;
    const kontakt = prev.kontakt ?? resolvedKontakt ?? null;
    const slots: LinkSlots = { ...prev, kontakt, vertrag, vertraege };
    const patch: LinkPatch = { contractIds: vertraege.map((v) => v.id) };
    if ((vertrag?.id ?? null) !== (prev.vertrag?.id ?? null)) patch.contractId = vertrag?.id ?? null;
    if (!prev.kontakt && kontakt) patch.contactId = kontakt.id;
    return { slots, patch };
}

/**
 * Slot leeren — Kontakt-Leeren leert alles; Risiko/Vertrag nur sich selbst.
 * „Leeren" entfernt ALLE Zuordnungen der Sorte (Einzel-Entfernen macht das
 * Verwaltungs-Popover über removeRisiko/removeVertrag).
 */
export function clearSlot(prev: LinkSlots, slot: "kontakt" | "risiko" | "vertrag"): CascadeResult {
    if (slot === "kontakt") {
        const patch: LinkPatch = { contactId: null, contactIds: [] };
        if (prev.risiko || prev.risiken.length) {
            patch.riskObjectId = null;
            patch.riskObjectIds = [];
        }
        if (prev.vertrag || prev.vertraege.length) {
            patch.contractId = null;
            patch.contractIds = [];
        }
        return { slots: { ...EMPTY_SLOTS }, patch };
    }
    if (slot === "risiko") {
        return {
            slots: { ...prev, risiko: null, risiken: [] },
            patch: { riskObjectId: null, riskObjectIds: [] },
        };
    }
    return {
        slots: { ...prev, vertrag: null, vertraege: [] },
        patch: { contractId: null, contractIds: [] },
    };
}

/** Slots aus einem Vorgang-/Todo-DTO ableiten (Server liefert kunde/risiko/vertrag-
 *  Summaries; seit SP3 zusätzlich risiken/vertraege für ALLE Zuordnungen). */
export function slotsFromDto(dto: {
    kunde?: {
        id: number;
        lastname?: string | null;
        firstName?: string | null;
        type?: { type?: string | null } | null;
        status?: { status?: string | null } | null;
        boType?: "NONE" | "USER" | "TEAM" | null;
        boTeam?: { id: string; name?: string | null } | null;
        boUser?: { userId: string; kuerzel?: string | null; displayName: string } | null;
        foUser?: { userId: string; kuerzel?: string | null; displayName: string } | null;
    } | null;
    kontakte?: Array<{
        id: number;
        lastname?: string | null;
        firstName?: string | null;
        boType?: "NONE" | "USER" | "TEAM" | null;
        boTeam?: { id: string; name?: string | null } | null;
        boUser?: { userId: string; kuerzel?: string | null; displayName: string } | null;
        foUser?: { userId: string; kuerzel?: string | null; displayName: string } | null;
    }> | null;
    risiko?: RisikoRef | null;
    vertrag?: VertragRef | null;
    risiken?: RisikoRef[] | null;
    vertraege?: VertragRef[] | null;
}): LinkSlots {
    const risiken = dto.risiken?.length ? dto.risiken : dto.risiko ? [dto.risiko] : [];
    const vertraege = dto.vertraege?.length ? dto.vertraege : dto.vertrag ? [dto.vertrag] : [];
    const kontakt = dto.kunde
            ? {
                  id: dto.kunde.id,
                  lastname: dto.kunde.lastname,
                  firstName: dto.kunde.firstName,
                  typ: dto.kunde.type?.type ?? null,
                  kundeStatus: dto.kunde.status?.status ?? null,
                  boType: dto.kunde.boType ?? null,
                  boTeam: dto.kunde.boTeam ?? null,
                  boUser: dto.kunde.boUser ?? null,
                  foUser: dto.kunde.foUser ?? null,
              }
            : null;
    const weitereKontakte = (dto.kontakte ?? [])
        .filter((k) => k.id !== kontakt?.id)
        .map((k) => ({ ...k }));
    const kontakte = kontakt ? [kontakt, ...weitereKontakte] : weitereKontakte;
    return {
        kontakt: kontakt ?? kontakte[0] ?? null,
        kontakte,
        risiko: dto.risiko ?? risiken[0] ?? null,
        vertrag: dto.vertrag ?? vertraege[0] ?? null,
        risiken,
        vertraege,
    };
}
